import * as React from "react";

/**
 * The desktop window's FALLBACK drag strip — an invisible band pinned to the top
 * edge that carries `-webkit-app-region: drag`, so the frameless Electron window
 * (desktop/main.js `titleBarStyle: 'hidden'`) stays MOVABLE on every screen.
 *
 * There is deliberately NO visible title bar. The elegant, Linear-style chrome is
 * achieved by making the app's OWN top nav the drag surface (see globals.css
 * `.app-titlebar-drag` — the nav header drags, its buttons opt out with no-drag,
 * and the macOS traffic lights sit inside the nav row). This strip is the safety
 * net for the chrome-free TAKEOVER screens (login / welcome / onboarding) that
 * render no nav — without it, the window would be unmovable there. It is
 * transparent + borderless, so it never reads as a "bar"; it only provides drag.
 *
 * ROBUST BY CONSTRUCTION (do not regress to a hydration-gated element): this
 * markup renders UNCONDITIONALLY — it is in the server HTML from the first paint —
 * and all of its desktop-only presentation lives in CSS keyed on
 * `html[data-desktop-chrome="1"]`, which the Electron preload stamps BEFORE React
 * hydrates. In a plain browser the attribute is absent, so the strip is
 * `display:none` (zero layout impact) and the web layout is unchanged.
 *
 * Append `?desktopchrome=1` to any URL to preview the desktop chrome in a browser.
 */
export function useIsDesktopShell(): boolean {
  const [isDesktop, setIsDesktop] = React.useState(false);
  React.useEffect(() => {
    const w = window as unknown as { excellentDesktop?: { isDesktop?: boolean } };
    const forced =
      typeof location !== "undefined" && location.search.includes("desktopchrome=1");
    setIsDesktop(Boolean(w.excellentDesktop?.isDesktop) || forced);
  }, []);
  return isDesktop;
}

export function DesktopTitleBar() {
  React.useEffect(() => {
    // Electron stamps `data-desktop-chrome` + `platform-<os>` in preload.js (before
    // hydration) and OWNS them there — so this effect handles ONLY the browser
    // preview path (`?desktopchrome=1`), where there is no preload. It mimics macOS
    // (platform-darwin) so the traffic-light inset is previewable. No-op in the shell.
    const w = window as unknown as { excellentDesktop?: { isDesktop?: boolean } };
    if (w.excellentDesktop?.isDesktop) return;
    if (typeof location === "undefined" || !location.search.includes("desktopchrome=1")) return;
    const html = document.documentElement;
    html.setAttribute("data-desktop-chrome", "1");
    html.classList.add("platform-darwin");
    return () => {
      html.removeAttribute("data-desktop-chrome");
      html.classList.remove("platform-darwin");
    };
  }, []);

  // Rendered unconditionally; CSS reveals it (transparent, drag) only in the shell.
  return <div className="desktop-titlebar" role="presentation" aria-hidden />;
}
